import { exactInteger } from '@/lib/exact'
import type {
  Block, Cell, ChipCell, Column, DecideReason, FormBlock, NoteBlock, TableBlock,
} from '@/kova/spec/types'
import type { ApiClient } from '@/lib/api'
import { ApiError } from '@/lib/api/errors'

import type { Caller } from './handles'
import type { DataSource, ScreenData, ScreenLoader } from './provenance'
import { readAtStamp } from './provenance'
import { callerBeside, submitWrite, writeRefusal } from './writes'

/**
 * Support › working a contact, and the deflection routing behind
 * `sp-deflection`.
 *
 * # Two permissions on one section, and they are not interchangeable
 *
 * The reads and the writes split the way the help centre's do, with one
 * addition the backend calls out:
 *
 * > *"WORKING A CONTACT IS ITS OWN PERMISSION… Reading a queue of tickets and
 * > picking one up are different acts: the second attributes a person to a
 * > customer's request, and it must be possible to give somebody the queue
 * > without giving them the ability to mark a request handled."*
 *
 * So the status control is Support (`support.contact.work`) and the three
 * routing controls are Ops (`help.article.publish`), and each names its own
 * when it refuses. One role named for both would send half the people asking to
 * the wrong person.
 *
 * # Forward only, and no reopen
 *
 * `acknowledged` then `resolved`, enforced by a database trigger rather than by
 * the handler — *"this method's job is to turn a constraint into a sentence."*
 *
 * > *"There is no reopen: a resolved contact reopened would lose who resolved
 * > it and when, and a customer who needs more help files another one — which
 * > costs them one tap and costs the record nothing."*
 *
 * A resolved contact therefore gets NO control rather than a disabled one. The
 * distinction is the one the account-state work settled: a disabled control
 * says "you lack permission", and the truth here is "this is not a move that
 * exists".
 *
 * # Retiring an intent is not deleting it, and the reason is a shipped app
 *
 * > *"`retire` is the operation that exists instead of DELETE. It closes the
 * > live routing and leaves the key resolvable, so a shipped app build that
 * > still sends it degrades to 'no guides, here is a person' rather than to an
 * > error."*
 *
 * That is the difference between retiring an intent and breaking every phone
 * that has not updated, so it is said at the control.
 */

const PERM_WORK = 'support.contact.work'
const PERM_PUBLISH = 'help.article.publish'

export const SUPPORT_INTENTS_PATH = '/support/intents'

export function supportContactStatusPath(reference: string): string {
  return `/support/contacts/${encodeURIComponent(reference)}/status`
}

export function supportIntentVersionsPath(key: string): string {
  return `${SUPPORT_INTENTS_PATH}/${encodeURIComponent(key)}/versions`
}

export function supportIntentRetirePath(key: string): string {
  return `${SUPPORT_INTENTS_PATH}/${encodeURIComponent(key)}/retire`
}

const INTENTS_SOURCE = 'postgres · support_intents (GET /v1/admin/support/intents)'
const AUTHORITY = 'operational'

// ---------------------------------------------------------------------------
// Working a contact
// ---------------------------------------------------------------------------

const ACKNOWLEDGE_REASONS: readonly DecideReason[] = [
  ['picked_up', 'Picking this up to work on it', ''],
  ['triaged', 'Triaged and assigned', ''],
  ['reopened_elsewhere', 'Related to something already being worked', ''],
]

const RESOLVE_REASONS: readonly DecideReason[] = [
  ['answered', 'The customer’s question was answered', ''],
  ['resolved_by_action', 'Something was done and the problem is gone', ''],
  ['duplicate', 'The same customer filed this twice', ''],
  ['no_response', 'We asked for more and heard nothing back', ''],
  ['out_of_scope', 'Not something we can help with, and they were told so', ''],
]

/** One contact, as far as the status control needs to know. */
export interface ContactStanding {
  readonly reference: string
  readonly status: string
  readonly handledBy: string
}

/**
 * Picking a contact up, or resolving it.
 *
 * One control taking the move rather than two controls, because the two moves
 * are the same act at different points and the state decides which is offered.
 * The select carries only the moves available FROM this contact's state, so an
 * operator is never shown a transition the trigger would refuse.
 */
export function contactStatusForm(
  api: ApiClient, caller: Caller, c: ContactStanding,
): FormBlock {
  const session = writeRefusal(caller, PERM_WORK, 'Support', 'Working a support contact')
  const moves = movesFrom(c.status)
  const blocked = session !== ''
    ? session
    : moves.length === 0
      ? stuckSentence(c)
      : ''

  return {
    t: 'form',
    title: `Work contact ${c.reference}`,
    lead: workLead(c),
    fields: [
      {
        name: 'status', label: 'Move it to', kind: 'select', required: true,
        options: moves,
        note: 'Forward only. There is no reopen, and there is no route for one — a resolved contact put back would lose who resolved it and when, and a customer who needs more help files another contact.',
      },
    ],
    reasons: c.status === 'acknowledged' ? RESOLVE_REASONS : ACKNOWLEDGE_REASONS,
    reasonNote: 'Recorded against the contact with your name. This is what the next person reads if it comes back.',
    auditLine: `admin.support.contact.status · actor · support_contact:${c.reference} · reason`,
    cta: 'Move it',
    ctaNote: 'Your name is attached to the move. Picking a contact up is what stops two people answering the same customer.',
    blocked,
    ...(blocked === '' ? { submit: statusSubmit(api, c) } : {}),
  }
}

/** The moves available from a state. Never a transition the trigger refuses. */
function movesFrom(status: string): readonly (readonly [string, string])[] {
  switch (status) {
    case 'open':
    case 'new':
    case '':
      return [
        ['acknowledged', 'acknowledged — I am picking this up'],
        ['resolved', 'resolved — this is finished'],
      ]
    case 'acknowledged':
      return [['resolved', 'resolved — this is finished']]
    default:
      return []
  }
}

function stuckSentence(c: ContactStanding): string {
  if (c.status === 'resolved') {
    return `Disabled — this contact is resolved${c.handledBy === '' ? '' : ` by ${c.handledBy}`}, and resolved is the end. There is no reopen on this console and no route for one: putting it back would lose who resolved it and when. If the customer still needs help they file another contact, which costs them one tap and costs the record nothing.`
  }
  return `Disabled — this contact is in a state this console does not recognise (${c.status === '' ? 'none stated' : c.status}), so it cannot say which moves are available. Acting on it blind is not something to offer.`
}

function workLead(c: ContactStanding): string {
  if (c.status === 'acknowledged') {
    return `${c.handledBy === '' ? 'Somebody' : c.handledBy} has this one. Resolving it records that it is finished, and by whom.`
  }
  return 'Nobody has picked this one up. Acknowledging it puts your name on it, which is the whole mechanism that stops two agents answering the same customer at once.'
}

function statusSubmit(api: ApiClient, c: ContactStanding) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(supportContactStatusPath(c.reference), {
      status: v['status'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The contact moved',
    (answer) => [
      ['contact', c.reference, ''],
      ['status', stringAt(answer, 'status') || (v['status'] ?? '—'), ''],
      ['handled by', stringAt(answer, 'handled_by') || 'you', 'attributed, and not reversible'],
    ],
  )
}

// ---------------------------------------------------------------------------
// Deflection routing — sp-deflection
// ---------------------------------------------------------------------------

interface Intent {
  readonly key: string
  readonly createdBy: string
  readonly createdAt: string
  /** `0` means retired: issued, and currently offering nothing. */
  readonly liveVersion: number | null
  readonly matchScreens: readonly string[]
  readonly matchErrorCodes: readonly string[]
  readonly articleKeys: readonly string[]
  readonly rank: number | null
  readonly publishedBy: string
  readonly liveSince: string
}

function readIntent(raw: unknown): Intent | null {
  if (typeof raw !== 'object' || raw === null) return null
  const o = raw as Record<string, unknown>
  const key = str(o['intent_key'])
  if (key === '') return null
  return {
    key,
    createdBy: str(o['created_by']),
    createdAt: str(o['created_at']),
    liveVersion: int(o['live_version']),
    matchScreens: strings(o['match_screens']),
    matchErrorCodes: strings(o['match_error_codes']),
    articleKeys: strings(o['article_keys']),
    rank: int(o['rank']),
    publishedBy: str(o['published_by']),
    liveSince: str(o['live_since']),
  }
}

const COLS: readonly Column[] = [
  ['Intent key', 'm', 'minmax(180px,1.1fr)'],
  ['Offering', 'c', '170px'],
  ['On these screens', 'x', 'minmax(180px,1.2fr)'],
  ['On these errors', 'x', 'minmax(160px,1fr)'],
  ['Guides offered', 'x', 'minmax(190px,1.3fr)'],
  ['Rank', 'n', '90px'],
  ['Live version', 'm', '120px'],
  ['Published by', 'x', 'minmax(140px,1fr)'],
]

function row(i: Intent): readonly Cell[] {
  return [
    i.key,
    offeringCell(i),
    list(i.matchScreens, 'any screen'),
    list(i.matchErrorCodes, 'any error'),
    list(i.articleKeys, '— none'),
    i.rank === null ? '—' : String(i.rank),
    i.liveVersion === null ? '— not stated' : i.liveVersion === 0 ? 'none live' : `v${String(i.liveVersion)}`,
    i.publishedBy === '' ? '— unattributed' : i.publishedBy,
  ]
}

/**
 * Whether this intent is offering anything, and the retired case.
 *
 * `live_version` of 0 means retired, and the backend carries it *"so a console
 * can see 'issued and currently offering nothing' without opening it: that
 * state is legitimate — the guides it cited were withdrawn."* An intent with
 * no guides is drawn as a state rather than as an empty cell, because "offering
 * nothing" and "we could not read what it offers" lead somewhere different.
 */
function offeringCell(i: Intent): ChipCell {
  if (i.liveVersion === null) return ['— not stated', 'r']
  if (i.liveVersion === 0) return ['retired — offers nothing', 'a']
  if (i.articleKeys.length === 0) return ['live, no guides', 'a']
  return [`${String(i.articleKeys.length)} guide(s)`, 'g']
}

const RETIRE_IS_NOT_DELETE: NoteBlock = {
  t: 'note',
  tone: 'n',
  quiet: true,
      kicker: 'Retiring',
  title: 'A retired intent still resolves, and that is the point',
  body: 'Retiring closes the live routing and leaves the key answerable. An app build already in somebody’s pocket that still sends that key gets "no guides, here is a person" rather than an error — which is why there is no delete here and no route for one. An intent showing "retired" is a working row, not a broken one.',
  ref: 'retire is what exists instead of DELETE',
}

export function composeSupportIntents(
  payload: Record<string, unknown>, readAt: Date, caller: Caller, api: ApiClient,
): ScreenData {
  const raw = Array.isArray(payload['intents']) ? (payload['intents'] as readonly unknown[]) : []
  const rows: Intent[] = []
  let unreadable = 0
  for (const entry of raw) {
    const parsed = readIntent(entry)
    if (parsed === null) unreadable += 1
    else rows.push(parsed)
  }

  const live = rows.filter((i) => (i.liveVersion ?? 0) > 0).length
  const table: TableBlock = {
    t: 'table',
    title: 'Deflection routing',
    src: 'ledger',
    facets: false,
    cols: COLS,
    rows: rows.map(row),
    footer: `${String(rows.length)} intent(s) · ${String(live)} offering guides${unreadable === 0 ? '' : ` · ${String(unreadable)} unreadable`}`,
    foot2:
      'An intent matches on the screen a customer was looking at and the error they saw, and offers the guides it names before they reach a person. Rank orders intents that match the same moment; lower runs first.',
  }

  const blocks: Block[] = [table, RETIRE_IS_NOT_DELETE]

  if (unreadable > 0) {
    blocks.push({
      t: 'note',
      tone: 'r',
      kicker: 'Partial read',
      title: `${String(unreadable)} intent row(s) could not be read`,
      body: 'Counted rather than folded in. A row with no key is unusable to every control here — publishing and retiring both address an intent by key.',
      ref: 'the count is the finding',
    })
  }

  if (rows.length === 0 && unreadable === 0) {
    blocks.push({
      t: 'note',
      tone: 'a',
      kicker: 'Deflection',
      title: 'No intent is routing anything',
      body: 'Nothing is configured, so every customer who asks for help reaches a person without being offered a guide first. That is a working system rather than a broken one — but it is also the state where the deflection numbers are zero for a reason nobody has looked at.',
      ref: 'a counted zero, and probably a finding',
    })
  }

  blocks.push(
    createIntentForm(api, caller),
    publishRoutingForm(api, caller, rows),
    retireIntentForm(api, caller, rows),
  )

  const missing: string[] = []
  if (unreadable > 0) missing.push(`${String(unreadable)} unreadable row(s)`)
  if (rows.some((i) => i.liveVersion === null)) missing.push('whether at least one intent is live')

  const provenance: DataSource = missing.length === 0
    ? { kind: 'live', source: INTENTS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt) }
    : { kind: 'partial', source: INTENTS_SOURCE, authority: AUTHORITY, readAt: readAtStamp(readAt), missing }
  return { blocks, provenance }
}

export const loadSupportIntents: ScreenLoader = async (api) => {
  const calling = callerBeside(api)
  let payload: unknown
  try {
    payload = await api.get<unknown>(SUPPORT_INTENTS_PATH)
  } catch (error) {
    if (error instanceof ApiError) {
      return unreadableScreen(
        `The support intents endpoint answered ${String(error.status)}${error.code === null ? '' : ` (${error.code})`}.`,
        error.decisionRef,
      )
    }
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    return unreadableScreen('The backend could not be reached.', null)
  }
  if (typeof payload !== 'object' || payload === null) {
    return unreadableScreen('The support intents endpoint returned a shape this console does not know.', null)
  }
  return composeSupportIntents(payload as Record<string, unknown>, new Date(), await calling, api)
}

const INTENT_REASONS: readonly DecideReason[] = [
  ['new_deflection', 'A new moment worth offering guides at', ''],
  ['support_volume', 'Enough contacts arrive from here to route them', ''],
  ['product_change', 'A change in the app created a new moment', ''],
]

const PUBLISH_REASONS: readonly DecideReason[] = [
  ['routing_change', 'Different guides suit this moment better', ''],
  ['article_withdrawn', 'A guide it cited is gone', ''],
  ['match_widened', 'It should match more situations', ''],
  ['match_narrowed', 'It was matching too much', ''],
  ['rank_change', 'It should run before or after another intent', ''],
]

const RETIRE_REASONS: readonly DecideReason[] = [
  ['no_longer_relevant', 'The moment it routed no longer happens', ''],
  ['deflection_ineffective', 'Customers were not helped by it', ''],
  ['superseded', 'Another intent covers this better', ''],
  ['issued_in_error', 'It should not have existed', ''],
]

/** Issuing a key. It routes nothing until a version is published against it. */
function createIntentForm(api: ApiClient, caller: Caller): FormBlock {
  const blocked = writeRefusal(caller, PERM_PUBLISH, 'Ops', 'Issuing an intent key')
  return {
    t: 'form',
    title: 'Issue an intent key',
    lead: 'Creates the key an app sends when a customer asks for help at a particular moment. It routes nothing on its own — publishing a version against it, below, is what decides which guides that moment offers.',
    fields: [
      {
        name: 'intent_key', label: 'Intent key', kind: 'text', required: true, mono: true,
        note: 'What the app will send. It cannot be renamed afterwards, and a shipped build sending an old key keeps resolving to it, so this string outlives the decision.',
      },
    ],
    reasons: INTENT_REASONS,
    reasonNote: 'Recorded on the intent.',
    auditLine: 'admin.support.intent.create · actor · support_intent:<key> · reason',
    cta: 'Issue key',
    ctaNote: 'Nothing is offered to anybody until a routing version is published for it.',
    blocked,
    ...(blocked === '' ? { submit: createIntentSubmit(api) } : {}),
  }
}

function createIntentSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(SUPPORT_INTENTS_PATH, {
      intent_key: v['intent_key'] ?? '',
      reason: v['reason'] ?? '',
    }),
    'The intent key was issued',
    (answer) => [
      ['intent', stringAt(answer, 'intent_key') || (v['intent_key'] ?? '—'), ''],
      ['offering', 'nothing yet', 'publish a routing version to give it guides'],
    ],
  )
}

/**
 * Publishing the routing for an intent.
 *
 * Versioned (law 6): the control is "Save as new version" and diffs against
 * what is live. Editing a published version is not an operation the family has.
 */
function publishRoutingForm(
  api: ApiClient, caller: Caller, intents: readonly Intent[],
): FormBlock {
  const session = writeRefusal(caller, PERM_PUBLISH, 'Ops', 'Publishing deflection routing')
  const blocked = session !== ''
    ? session
    : intents.length === 0
      ? 'Disabled — no intent key exists to publish routing against. Issue one above first.'
      : ''
  const live = intents[0]

  return {
    t: 'form',
    title: 'Publish routing for an intent',
    lead: 'Decides which guides a customer is offered at this moment, and on which screens and errors it applies. Publishing replaces what is live from the boundary you choose; the version in force now stays on the record and keeps explaining what somebody was offered last week.',
    fields: [
      {
        name: 'intent_key', label: 'Intent', kind: 'select', required: true,
        options: intents.map((i) => [
          i.key,
          `${i.key} — ${(i.liveVersion ?? 0) === 0 ? 'offering nothing' : `v${String(i.liveVersion)}`}`,
        ] as const),
      },
      {
        name: 'match_screens', label: 'Screens it applies to', kind: 'text', required: false,
        mono: true, value: (live?.matchScreens ?? []).join(', '),
        note: 'Comma-separated. Empty means every screen — which is a real choice and a wide one.',
      },
      {
        name: 'match_error_codes', label: 'Errors it applies to', kind: 'text', required: false,
        mono: true, value: (live?.matchErrorCodes ?? []).join(', '),
        note: 'Comma-separated. Empty means any error, including none.',
      },
      {
        name: 'article_keys', label: 'Guides to offer', kind: 'text', required: true,
        mono: true, value: (live?.articleKeys ?? []).join(', '),
        note: 'Comma-separated help article keys, in the order they should appear. A guide that has been withdrawn will simply not be offered — the intent stays live and offers the rest.',
      },
      {
        name: 'rank', label: 'Rank', kind: 'text', required: true, mono: true,
        value: live?.rank === null || live?.rank === undefined ? '' : String(live.rank),
        note: 'Orders intents that match the same moment. Lower runs first.',
      },
    ],
    reasons: PUBLISH_REASONS,
    reasonNote: 'Recorded on the version.',
    auditLine: 'admin.support.routing.publish · actor · support_intent:<key> · reason',
    cta: 'Save as new version',
    ctaNote: 'The version in force now is not changed — it is superseded, and stays readable.',
    blocked,
    versioned: true,
    baseline: [
      ['Screens it applies to', (live?.matchScreens ?? []).join(', ')],
      ['Errors it applies to', (live?.matchErrorCodes ?? []).join(', ')],
      ['Guides to offer', (live?.articleKeys ?? []).join(', ')],
      ['Rank', live?.rank === null || live?.rank === undefined ? '' : String(live.rank)],
    ],
    ...(blocked === '' ? { submit: publishRoutingSubmit(api) } : {}),
  }
}

function publishRoutingSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(supportIntentVersionsPath(v['intent_key'] ?? ''), {
      match_screens: csv(v['match_screens']),
      match_error_codes: csv(v['match_error_codes']),
      article_keys: csv(v['article_keys']),
      rank: /^-?\d+$/.test((v['rank'] ?? '').trim()) ? Number((v['rank'] ?? '').trim()) : 0,
      reason: v['reason'] ?? '',
    }),
    'The routing was published',
    (answer) => {
      const version = numberAt(answer, 'live_version')
      return [
        ['intent', stringAt(answer, 'intent_key') || (v['intent_key'] ?? '—'), ''],
        ...(version === null
          ? []
          : [['live version', String(version), 'allocated by the store'] as const]),
        ['guides offered', csv(v['article_keys']).join(', ') || '— none', ''],
      ]
    },
  )
}

/** Stopping an intent offering anything, without breaking the key. */
function retireIntentForm(
  api: ApiClient, caller: Caller, intents: readonly Intent[],
): FormBlock {
  const session = writeRefusal(caller, PERM_PUBLISH, 'Ops', 'Retiring an intent')
  const offering = intents.filter((i) => (i.liveVersion ?? 0) > 0)
  const blocked = session !== ''
    ? session
    : offering.length === 0
      ? 'Disabled — no intent is currently offering anything, so there is no live routing to close.'
      : ''

  return {
    t: 'form',
    title: 'Retire an intent',
    lead: 'Closes the live routing so this moment stops offering guides. It does NOT delete the key: an app build already in somebody’s pocket that still sends it gets "no guides, here is a person" rather than an error. That is the whole reason this is retire and not delete.',
    fields: [
      {
        name: 'intent_key', label: 'Intent', kind: 'select', required: true,
        options: offering.map((i) => [i.key, `${i.key} — v${String(i.liveVersion)}`] as const),
        note: 'Only intents that are currently offering something. One already retired has nothing to close.',
      },
    ],
    reasons: RETIRE_REASONS,
    reasonNote: 'Recorded on the retirement.',
    auditLine: 'admin.support.intent.retire · actor · support_intent:<key> · reason',
    cta: 'Retire',
    ctaNote: 'Reversible by publishing a new version against the same key. The versions it had stay on the record.',
    blocked,
    ...(blocked === '' ? { submit: retireSubmit(api) } : {}),
  }
}

function retireSubmit(api: ApiClient) {
  return (v: Readonly<Record<string, string>>) => submitWrite(
    () => api.post<unknown>(supportIntentRetirePath(v['intent_key'] ?? ''), {
      reason: v['reason'] ?? '',
    }),
    'The intent was retired',
    () => [
      ['intent', v['intent_key'] ?? '—', 'still resolves, and now offers nothing'],
      ['customers see', 'no guides, and a route to a person', ''],
    ],
  )
}

// ---------------------------------------------------------------------------

/**
 * A comma-separated field as the array the body wants.
 *
 * Trimmed and emptied, so `"a, ,b"` is two entries rather than three. A blank
 * entry in a match list would match nothing and read as if it matched
 * everything, which is the wrong way round for a list that decides what a
 * customer is shown.
 */
function csv(raw: string | undefined): readonly string[] {
  return (raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s !== '')
}

function list(values: readonly string[], empty: string): Cell {
  return values.length === 0 ? empty : values.join(', ')
}

function unreadableScreen(reason: string, decisionRef: string | null): ScreenData {
  return {
    blocks: [{
      t: 'note',
      tone: 'r',
      kicker: 'Not read',
      title: 'The deflection routing could not be read',
      body: `${reason} No control is drawn: publishing and retiring both address an intent by key, and this read is where the keys come from.`,
      ref: decisionRef === null ? 'no decision reference was supplied' : `decision_ref ${decisionRef}`,
    }],
    provenance: { kind: 'unread', source: INTENTS_SOURCE, reason },
  }
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function int(v: unknown): number | null {
  return exactInteger(v)
}

function strings(v: unknown): readonly string[] {
  return Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : []
}

function stringAt(o: unknown, key: string): string {
  if (typeof o !== 'object' || o === null) return ''
  const value = (o as Record<string, unknown>)[key]
  return typeof value === 'string' ? value : ''
}

function numberAt(o: unknown, key: string): number | null {
  if (typeof o !== 'object' || o === null) return null
  const value = (o as Record<string, unknown>)[key]
  return exactInteger(value)
}
